import { useCallback } from 'react';
import { TValidationMethod } from 'js/utils/Validation/types';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from './helpers';
import { useGetFormFieldValue } from '../../../../hooks';
import { useUpdateFieldValue } from '../useUpdateFieldValue';
import { useWithRecordLevelValidation } from '../useWithRecordLevelValidation';

export const useSpanishIdFieldValidation = ({ fieldName }: { fieldName: string }) => {
    const DNI_LENGTH = 9;
    const translate = useComponentTranslations();
    const getFormFieldValue = useGetFormFieldValue();
    const updateFieldValue = useUpdateFieldValue({ fieldName });
    const withRecordLevelValidation = useWithRecordLevelValidation({ fieldName });

    const spanishIdValidate = useCallback<TValidationMethod<string>>(async (value) => {
        const countryCode = getFormFieldValue('countrycode');
        const citizenship = getFormFieldValue('citizenship');

        if (!(countryCode && citizenship)) {
            return translate('playtech.mobile.system.form.spanishID.internal.emptyCountryOrNational');
        }

        if (citizenship !== 'ES' && countryCode !== 'ES') {
            return;
        }

        if (citizenship === 'ES' && countryCode !== 'ES' && !value.length) {
            return;
        }

        if (citizenship === 'ES' && countryCode === 'ES' && !value.length) {
            return translate('playtech.mobile.system.form.spanishID.internal.required');
        }

        if (citizenship === 'ES' && value.length) {
            if (!helpers.checkDNI(value)) {
                return translate('playtech.mobile.system.form.spanishID.internal.regexp', ['DNI']);
            }

            value.length < DNI_LENGTH && updateFieldValue(String(`00000000${value}`).slice(-DNI_LENGTH));
        }

        if (citizenship !== 'ES' && countryCode === 'ES' && !value.length) {
            return translate('playtech.mobile.system.form.spanishID.internal.required');
        }

        if (citizenship !== 'ES' && countryCode === 'ES' && value.length && !helpers.checkNIE(value)) {
            return translate('playtech.mobile.system.form.spanishID.internal.regexp', ['NIE']);
        }
    }, [getFormFieldValue, translate, updateFieldValue]);

    return withRecordLevelValidation(spanishIdValidate);
};
